<template>
  <div class="free-bets" v-if="hasAvailable">
    <!-- Finché nessuna freebet è selezionata il blocco "chiama" l'utente: luce che scorre sul bordo + regalo animato -->
    <div
        v-if="sortedCompatible.length > 0"
        class="fb-sel"
        :class="{ 'fb-sel--idle': !getSelectedFreeBet }"
    >
      <div class="fb-sel__inner">
        <div class="fb-sel__head">
          <svg class="fb-sel__gift" viewBox="0 0 64 64" width="22" height="22" aria-hidden="true">
            <defs>
              <linearGradient id="fbSelBox" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#ff4d8d"/>
                <stop offset="1" stop-color="#ff8a3d"/>
              </linearGradient>
              <linearGradient id="fbSelLid" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#ff6aa3"/>
                <stop offset="1" stop-color="#ffa45c"/>
              </linearGradient>
            </defs>
            <rect x="12" y="30" width="40" height="28" rx="4" fill="url(#fbSelBox)"/>
            <rect x="9" y="22" width="46" height="11" rx="3" fill="url(#fbSelLid)"/>
            <rect x="28" y="22" width="8" height="36" fill="#ffd23f"/>
            <path d="M32 22c-4-9-14-11-14-4 0 4 7 5 14 4z" fill="#ffd23f"/>
            <path d="M32 22c4-9 14-11 14-4 0 4-7 5-14 4z" fill="#ffc107"/>
          </svg>
          <span class="fb-sel__title">{{ $t('freeBets.title') }}</span>
          <span class="fb-sel__count">{{ sortedCompatible.length }}</span>
        </div>

        <div class="fb-sel__list" :class="{ 'fb-sel__list--stacked': hasMore }">
          <FreeBetCard
              v-for="token in visibleTokens"
              :key="token.tokenId"
              :token="token"
              selectable
              :show-rules="false"
              :selected="isSelected(token)"
              @toggle="toggle(token, $event)"
          ></FreeBetCard>
        </div>

        <button v-if="hasMore" type="button" class="fb-sel__more" @click="showModal = true">
          <span>{{ $t('freeBets.showAll', { count: getAvailableFreeBets.length }) }}</span>
          <v-icon x-small>mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>

    <!-- Nessuna compatibile col carrello ma l'utente ne ha: solo il link alla modale -->
    <button v-else type="button" class="fb-sel__more fb-sel__more--plain" @click="showModal = true">
      <span>{{ $t('freeBets.showAll', { count: getAvailableFreeBets.length }) }}</span>
      <v-icon x-small>mdi-chevron-right</v-icon>
    </button>

    <FreeBetsModal v-model="showModal"></FreeBetsModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FreeBetCard from "@/components/right_widgets/FreeBetCard";
import FreeBetsModal from "@/components/right_widgets/FreeBetsModal";

const PREVIEW_LIMIT = 2;

export default {
  name: "FreeBetsSelector",
  components: { FreeBetCard, FreeBetsModal },
  data: () => ({
    showModal: false,
  }),
  computed: {
    ...mapGetters({
      getBet: "cart/getBet",
      getSettings: "cart/getSettings",
      getAuthenticated: "user/getAuthenticated",
      getAvailableFreeBets: "cart/getAvailableFreeBets",
      getCompatibleFreeBets: "cart/getCompatibleFreeBets",
      getSelectedFreeBet: "cart/getSelectedFreeBet",
    }),
    hasAvailable() {
      return Array.isArray(this.getAvailableFreeBets) && this.getAvailableFreeBets.length > 0;
    },
    // La freebet selezionata resta sempre in cima, così è visibile anche nell'anteprima.
    sortedCompatible() {
      const list = [...this.getCompatibleFreeBets];
      const selectedIndex = list.findIndex(t => this.isSelected(t));
      if (selectedIndex > 0) list.unshift(list.splice(selectedIndex, 1)[0]);
      return list;
    },
    // Oltre il limite si mostra solo la prima (le altre "impilate" sotto) e il pulsante apre la modale.
    visibleTokens() {
      return this.sortedCompatible.length > PREVIEW_LIMIT ? this.sortedCompatible.slice(0, 1) : this.sortedCompatible;
    },
    hasMore() {
      return this.getAvailableFreeBets.length > this.visibleTokens.length;
    },
    // Ogni variazione di tipo, quote o importo richiede una nuova verifica di compatibilità.
    cartSignature() {
      return [
        this.getBet.type,
        this.getBet.odds.map(o => o.unique_id).join('|'),
        Number(this.getBet._multiple_detail.amount) || 0,
      ].join('#');
    },
  },
  watch: {
    cartSignature() {
      this.scheduleCompatibleFreeBets();
    },
    getAuthenticated(val) {
      if (val) this.reloadFreeBets();
    },
  },
  async mounted() {
    if (this.getAvailableFreeBets === null) {
      await this.fetchAvailableFreeBets();
    }
    this.scheduleCompatibleFreeBets();
  },
  methods: {
    ...mapActions({
      fetchAvailableFreeBets: "cart/fetchAvailableFreeBets",
      scheduleCompatibleFreeBets: "cart/scheduleCompatibleFreeBets",
      selectFreeBet: "cart/selectFreeBet",
      reloadFreeBets: "cart/reloadFreeBets",
    }),
    isSelected(token) {
      return !!this.getSelectedFreeBet && this.getSelectedFreeBet.tokenId === token.tokenId;
    },
    toggle(token, checked) {
      this.selectFreeBet(checked ? token.tokenId : null);
    },
  },
};
</script>

<style scoped>
.free-bets {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--hsla-1);
}

/* ── Contenitore (palette rosa / arancio / oro della promo freebet) ── */
.fb-sel {
  position: relative;
  border-radius: 12px;
  padding: 1px;
  overflow: hidden;
  isolation: isolate;
  background: rgba(255, 106, 163, 0.35);
}

.fb-sel__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 11px;
  background:
      linear-gradient(135deg, rgba(255, 77, 141, 0.14), rgba(255, 138, 61, 0.10) 55%, rgba(255, 210, 63, 0.05)),
      var(--bg-card);
}

/* Nessuna freebet selezionata: luce che scorre lungo il bordo */
.fb-sel--idle::before {
  content: '';
  position: absolute;
  z-index: 0;
  top: 50%;
  left: 50%;
  width: 250%;
  aspect-ratio: 1;
  background: conic-gradient(
      from 0deg,
      transparent 0deg 250deg,
      #ff4d8d 290deg,
      #ff8a3d 320deg,
      #ffd23f 345deg,
      transparent 360deg
  );
  transform: translate(-50%, -50%);
  animation: fbBorderSpin 4s linear infinite;
}

.fb-sel__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fb-sel__gift {
  flex-shrink: 0;
  transform-origin: 50% 90%;
}

.fb-sel--idle .fb-sel__gift {
  animation: fbGiftWiggle 2.8s ease-in-out infinite;
}

.fb-sel__title {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-3xl);
  font-weight: 700;
  background: linear-gradient(90deg, #ff6aa3, #ffb36b);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.fb-sel__count {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: linear-gradient(90deg, #ff4d8d, #ff8a3d);
  color: #fff;
  font-size: var(--font-size-xl);
  font-weight: 800;
  line-height: 20px;
  text-align: center;
}

.fb-sel__list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Altre freebet "impilate" sotto la prima: due bordi che sporgono */
.fb-sel__list--stacked {
  margin-bottom: 10px;
}

.fb-sel__list--stacked::before,
.fb-sel__list--stacked::after {
  content: '';
  position: absolute;
  left: 50%;
  height: 100%;
  border-radius: 8px;
  transform: translateX(-50%);
  pointer-events: none;
}

.fb-sel__list--stacked::before {
  z-index: -1;
  bottom: -5px;
  width: 94%;
  background: rgba(255, 138, 61, 0.22);
}

.fb-sel__list--stacked::after {
  z-index: -2;
  bottom: -10px;
  width: 86%;
  background: rgba(255, 77, 141, 0.14);
}

.fb-sel__more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 1px dashed rgba(255, 138, 61, 0.45);
  border-radius: 8px;
  background: rgba(255, 138, 61, 0.06);
  color: #ffb36b;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.fb-sel__more:hover {
  background: rgba(255, 138, 61, 0.12);
  border-color: rgba(255, 138, 61, 0.7);
}

.fb-sel__more .v-icon {
  color: inherit;
}

.fb-sel__more--plain {
  border-style: solid;
}

@keyframes fbBorderSpin {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes fbGiftWiggle {
  0%, 70%, 100% { transform: rotate(0deg); }
  76% { transform: rotate(-9deg); }
  82% { transform: rotate(8deg); }
  88% { transform: rotate(-5deg); }
  94% { transform: rotate(2deg); }
}

@media (prefers-reduced-motion: reduce) {
  .fb-sel--idle::before,
  .fb-sel--idle .fb-sel__gift {
    animation: none;
  }
}
</style>
